import React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useActiveOrganization } from "@/contexts"
import { Form, FormTextField, SubmitButton, useForm } from "@/nzds"
import { DialogTitle } from "@/nzds/feedback/dialog-title/dialog-title"
import { useSendContactEmail } from "@/services/email/useSendContactEmail"

import { Dialog, DialogActions, DialogContent, Typography } from "@mui/material"

import { If } from "../if/if"
import type {
  ContactDialogProps,
  ContactDialogType,
} from "./contact-dialog.types"
import { contactSchema } from "./contact.schema"
import type { ContactSchema } from "./contact.schema"

const typeToTitle: Record<ContactDialogType, string> = {
  bps: "contactSupport.titleBps",
  decarbonize: "contactSupport.titleDecarbonize",
  support: "contactSupport.title",
} as const

const typeToDescription: Record<ContactDialogType, string> = {
  bps: "contactSupport.descriptionBps",
  decarbonize: "contactSupport.descriptionDecarbonize",
  support: "contactSupport.description",
} as const

export const ContactDialog: React.FC<ContactDialogProps> = ({
  onClose,
  open,
  type,
}) => {
  const { t } = useTranslation()
  const { activeOrganization } = useActiveOrganization()
  const [isSent, setIsSent] = React.useState(false)

  const defaultFormValues: ContactSchema = {
    message: "",
    type,
  }

  const form = useForm({
    defaultValues: defaultFormValues,
    mode: "all",
    schema: contactSchema,
  })

  const { sendContactEmail, sendContactEmailIsPending } = useSendContactEmail({
    onError: (error) => {
      console.error(error)
    },
    onSuccess: () => {
      setIsSent(true)
    },
  })

  const handleClose = () => {
    onClose()
  }

  const handleSubmit: SubmitHandler<ContactSchema> = async (
    value: ContactSchema
  ): Promise<void> => {
    const message = value.message?.trim()
    if (!message) {
      return
    }
    const dialogType = value.type ?? type
    const organizationId = activeOrganization?.id
    if (!organizationId) {
      return
    }

    await sendContactEmail({ message, organizationId, type: dialogType })
  }

  const handleExited = (): void => {
    form.reset()
    setIsSent(false)
  }

  return (
    <Dialog
      aria-describedby="contact-dialog-description"
      aria-labelledby="contact-dialog-title"
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      slotProps={{
        transition: {
          onExited: handleExited,
        },
      }}
    >
      <If condition={!isSent}>
        <DialogTitle id="contact-dialog-title" onClose={handleClose}>
          {t(typeToTitle[type], { ns: "common" })}
        </DialogTitle>
        <Form
          aria-label={t(typeToTitle[type], { ns: "common" })}
          form={form}
          onSubmit={handleSubmit}
        >
          <DialogContent id="contact-dialog-description">
            <Typography
              sx={{
                mb: 3,
              }}
            >
              {t(typeToDescription[type], { ns: "common" })}
            </Typography>
            <FormTextField
              fullWidth
              id="message"
              label=""
              maxRows={12}
              minRows={4}
              multiline
              name="message"
              placeholder={t("contactSupport.messagePlaceholder", {
                ns: "common",
              })}
              preserveHelperTextSpacing
              variant="stacked"
            />
          </DialogContent>
          <DialogActions>
            <SubmitButton
              disabled={sendContactEmailIsPending}
              loading={sendContactEmailIsPending}
            >
              {t("contactSupport.submitButton", { ns: "common" })}
            </SubmitButton>
          </DialogActions>
        </Form>
      </If>
      <If condition={isSent}>
        <DialogTitle id="contact-dialog-title" onClose={handleClose}>
          {t("contactSupport.successTitle", { ns: "common" })}
        </DialogTitle>
        <DialogContent id="contact-dialog-description">
          <Typography
            sx={{
              mb: 2,
            }}
          >
            {t("contactSupport.successMessage", { ns: "common" })}
          </Typography>
        </DialogContent>
      </If>
    </Dialog>
  )
}
